import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import {
  Calculator,
  Crosshair,
  Info,
  Mars,
  Ruler,
  Scale,
  Target,
  Venus,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/body-fat-calculator")({
  component: BodyFatCalculator,
  head: () => ({
    meta: seo({
      title:
        "Vücut Yağ Oranı Hesaplayıcı - Body Fat Calculator | AtalayTheCoach",
      description:
        "Vücut yağ oranınızı Jackson-Pollock, Navy ve BMI yöntemleriyle bilimsel olarak hesaplayın. Vücut kompozisyonunuzu analiz edin.",
      keywords:
        "vücut yağ oranı, body fat hesaplama, kaliper ölçümü, navy method, vücut kompozisyonu, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/body-fat-calculator",
    }),
  }),
});

// Jackson-Pollock 3-Site Formülü (Kaliper)
function calculateJacksonPollock3Site(
  age: number,
  gender: string,
  chest: number,
  abdomen: number,
  thigh: number,
): number {
  if (gender === "male") {
    const density =
      1.10938 -
      0.0008267 * (chest + abdomen + thigh) +
      0.0000016 * (chest + abdomen + thigh) ** 2 -
      0.0002574 * age;
    return 495 / density - 450;
  } else {
    const density =
      1.0994921 -
      0.0009929 * (chest + abdomen + thigh) +
      0.0000023 * (chest + abdomen + thigh) ** 2 -
      0.0001392 * age;
    return 495 / density - 450;
  }
}

// Jackson-Pollock 7-Site Formülü (Kaliper)
function calculateJacksonPollock7Site(
  age: number,
  gender: string,
  measurements: any,
): number {
  const sum =
    measurements.chest +
    measurements.midaxillary +
    measurements.triceps +
    measurements.subscapular +
    measurements.abdomen +
    measurements.suprailiac +
    measurements.thigh;

  if (gender === "male") {
    const density =
      1.112 - 0.00043499 * sum + 0.00000055 * sum ** 2 - 0.00028826 * age;
    return 495 / density - 450;
  } else {
    const density =
      1.097 - 0.00046971 * sum + 0.00000056 * sum ** 2 - 0.00012828 * age;
    return 495 / density - 450;
  }
}

// Navy Method (Çevre Ölçümleri)
function calculateNavyMethod(
  gender: string,
  height: number,
  waist: number,
  neck: number,
  hip?: number,
): number {
  if (gender === "male") {
    return (
      495 /
        (1.0324 -
          0.19077 * Math.log10(waist - neck) +
          0.15456 * Math.log10(height)) -
      450
    );
  } else {
    if (!hip) return 0;
    return (
      495 /
        (1.29579 -
          0.35004 * Math.log10(waist + hip - neck) +
          0.221 * Math.log10(height)) -
      450
    );
  }
}

// Deurenberg Formülü (BMI Tabanlı)
function calculateDeurenberg(bmi: number, age: number, gender: string): number {
  const genderFactor = gender === "male" ? 1 : 0;
  return 1.2 * bmi + 0.23 * age - 10.8 * genderFactor - 5.4;
}

// Vücut Yağ Kategorileri
const getBodyFatCategory = (bodyFat: number, gender: string) => {
  const categories = {
    male: {
      essential: {
        min: 2,
        max: 5,
        label: "Esansiyel Yağ",
        color:
          "bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive",
      },
      athletes: {
        min: 6,
        max: 13,
        label: "Sporcu",
        color:
          "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary",
      },
      fitness: {
        min: 14,
        max: 17,
        label: "Fitness",
        color:
          "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary",
      },
      average: {
        min: 18,
        max: 24,
        label: "Ortalama",
        color:
          "bg-warning/10 text-warning-foreground dark:bg-warning/20 dark:text-warning",
      },
      obese: {
        min: 25,
        max: 100,
        label: "Obez",
        color:
          "bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive",
      },
    },
    female: {
      essential: {
        min: 10,
        max: 13,
        label: "Esansiyel Yağ",
        color:
          "bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive",
      },
      athletes: {
        min: 14,
        max: 20,
        label: "Sporcu",
        color:
          "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary",
      },
      fitness: {
        min: 21,
        max: 24,
        label: "Fitness",
        color:
          "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary",
      },
      average: {
        min: 25,
        max: 31,
        label: "Ortalama",
        color:
          "bg-warning/10 text-warning-foreground dark:bg-warning/20 dark:text-warning",
      },
      obese: {
        min: 32,
        max: 100,
        label: "Obez",
        color:
          "bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive",
      },
    },
  };

  const genderCategories = categories[gender as keyof typeof categories];

  for (const [_key, category] of Object.entries(genderCategories)) {
    if (bodyFat >= category.min && bodyFat <= category.max) {
      return category;
    }
  }

  return genderCategories.average;
};

function BodyFatCalculator() {
  const [activeMethod, setActiveMethod] = useState("jackson-pollock-3");
  const [gender, setGender] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [weight, setWeight] = useState<string>("");

  // Jackson-Pollock 3-Site
  const [chest3, setChest3] = useState<string>("");
  const [abdomen3, setAbdomen3] = useState<string>("");
  const [thigh3, setThigh3] = useState<string>("");

  // Jackson-Pollock 7-Site
  const [chest7, setChest7] = useState<string>("");
  const [midaxillary, setMidaxillary] = useState<string>("");
  const [triceps, setTriceps] = useState<string>("");
  const [subscapular, setSubscapular] = useState<string>("");
  const [abdomen7, setAbdomen7] = useState<string>("");
  const [suprailiac, setSuprailiac] = useState<string>("");
  const [thigh7, setThigh7] = useState<string>("");

  // Navy Method
  const [waist, setWaist] = useState<string>("");
  const [neck, setNeck] = useState<string>("");
  const [hip, setHip] = useState<string>("");

  const [results, setResults] = useState<{
    bodyFat: number;
    category: any;
    method: string;
    leanMass?: number;
    fatMass?: number;
  } | null>(null);

  const calculateBodyFat = () => {
    const ageNum = Number.parseFloat(age);
    const heightNum = Number.parseFloat(height);
    const weightNum = Number.parseFloat(weight);

    if (!ageNum || !gender) return;

    let bodyFat = 0;
    let methodName = "";

    switch (activeMethod) {
      case "jackson-pollock-3":
        if (!chest3 || !abdomen3 || !thigh3) return;
        bodyFat = calculateJacksonPollock3Site(
          ageNum,
          gender,
          Number.parseFloat(chest3),
          Number.parseFloat(abdomen3),
          Number.parseFloat(thigh3),
        );
        methodName = "Jackson-Pollock 3-Site";
        break;

      case "jackson-pollock-7":
        if (
          !chest7 ||
          !midaxillary ||
          !triceps ||
          !subscapular ||
          !abdomen7 ||
          !suprailiac ||
          !thigh7
        )
          return;
        bodyFat = calculateJacksonPollock7Site(ageNum, gender, {
          chest: Number.parseFloat(chest7),
          midaxillary: Number.parseFloat(midaxillary),
          triceps: Number.parseFloat(triceps),
          subscapular: Number.parseFloat(subscapular),
          abdomen: Number.parseFloat(abdomen7),
          suprailiac: Number.parseFloat(suprailiac),
          thigh: Number.parseFloat(thigh7),
        });
        methodName = "Jackson-Pollock 7-Site";
        break;

      case "navy":
        if (!heightNum || !waist || !neck || (gender === "female" && !hip))
          return;
        bodyFat = calculateNavyMethod(
          gender,
          heightNum,
          Number.parseFloat(waist),
          Number.parseFloat(neck),
          hip ? Number.parseFloat(hip) : undefined,
        );
        methodName = "Navy Method";
        break;

      case "deurenberg": {
        if (!heightNum || !weightNum) return;
        const bmi = weightNum / (heightNum / 100) ** 2;
        bodyFat = calculateDeurenberg(bmi, ageNum, gender);
        methodName = "Deurenberg (BMI)";
        break;
      }
    }

    if (bodyFat < 0) bodyFat = 0;
    if (bodyFat > 50) bodyFat = 50;

    const category = getBodyFatCategory(bodyFat, gender);

    let leanMass;
    let fatMass;
    if (weightNum) {
      fatMass = (weightNum * bodyFat) / 100;
      leanMass = weightNum - fatMass;
    }

    setResults({
      bodyFat,
      category,
      method: methodName,
      leanMass,
      fatMass,
    });
    trackCalculatorComplete("body_fat");
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Scale className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            Vücut Yağ Oranı Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Farklı yöntemlerle vücut yağ oranınızı hesaplayın. Kaliper
            ölçümleri, çevre ölçümleri veya BMI bazlı hesaplamalar arasından
            seçim yapın.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Hesaplayıcı Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-primary" />
                  Vücut Yağ Oranı Hesapla
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Temel Bilgiler */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <Label>Cinsiyet</Label>
                    <RadioGroup value={gender} onValueChange={setGender}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male-bf" />
                        <Label htmlFor="male-bf">Erkek</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female-bf" />
                        <Label htmlFor="female-bf">Kadın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age">Yaş</Label>
                    <Input
                      id="age"
                      type="number"
                      placeholder="25"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="height">Boy (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      placeholder="175"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="weight">Kilo (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="70"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                    />
                  </div>
                </div>

                {/* Hesaplama Yöntemleri */}
                <div className="space-y-4">
                  <Label className="font-semibold text-base">
                    Hesaplama Yöntemi
                  </Label>

                  <Tabs value={activeMethod} onValueChange={setActiveMethod}>
                    <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
                      <TabsTrigger value="jackson-pollock-3">
                        JP 3-Site
                      </TabsTrigger>
                      <TabsTrigger value="jackson-pollock-7">
                        JP 7-Site
                      </TabsTrigger>
                      <TabsTrigger value="navy">Navy</TabsTrigger>
                      <TabsTrigger value="deurenberg">BMI</TabsTrigger>
                    </TabsList>

                    {/* Jackson-Pollock 3-Site */}
                    <TabsContent
                      value="jackson-pollock-3"
                      className="space-y-4"
                    >
                      <div className="rounded-lg border border-secondary/20 bg-secondary/10 p-4">
                        <h4 className="mb-2 font-semibold">
                          Kaliper Ölçümleri (mm)
                        </h4>
                        <p className="mb-4 text-muted-foreground text-sm">
                          {gender === "male"
                            ? "Göğüs, karın ve uyluk bölgelerinden kaliper ile ölçüm yapın"
                            : "Triceps, uyluk ve suprailiac bölgelerinden kaliper ile ölçüm yapın"}
                        </p>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label htmlFor="chest3">
                              {gender === "male" ? "Göğüs" : "Triceps"}
                            </Label>
                            <Input
                              id="chest3"
                              type="number"
                              placeholder="10"
                              value={chest3}
                              onChange={(e) => setChest3(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="abdomen3">
                              {gender === "male" ? "Karın" : "Uyluk"}
                            </Label>
                            <Input
                              id="abdomen3"
                              type="number"
                              placeholder="15"
                              value={abdomen3}
                              onChange={(e) => setAbdomen3(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="thigh3">
                              {gender === "male" ? "Uyluk" : "Suprailiac"}
                            </Label>
                            <Input
                              id="thigh3"
                              type="number"
                              placeholder="12"
                              value={thigh3}
                              onChange={(e) => setThigh3(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Jackson-Pollock 7-Site */}
                    <TabsContent
                      value="jackson-pollock-7"
                      className="space-y-4"
                    >
                      <div className="rounded-lg border border-accent/20 bg-accent/10 p-4">
                        <h4 className="mb-2 font-semibold">
                          7-Nokta Kaliper Ölçümleri (mm)
                        </h4>
                        <p className="mb-4 text-muted-foreground text-sm">
                          Daha hassas sonuç için 7 farklı noktadan ölçüm yapın
                        </p>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label htmlFor="chest7">Göğüs</Label>
                            <Input
                              id="chest7"
                              type="number"
                              placeholder="10"
                              value={chest7}
                              onChange={(e) => setChest7(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="midaxillary">Midaxillary</Label>
                            <Input
                              id="midaxillary"
                              type="number"
                              placeholder="8"
                              value={midaxillary}
                              onChange={(e) => setMidaxillary(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="triceps">Triceps</Label>
                            <Input
                              id="triceps"
                              type="number"
                              placeholder="12"
                              value={triceps}
                              onChange={(e) => setTriceps(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subscapular">Subscapular</Label>
                            <Input
                              id="subscapular"
                              type="number"
                              placeholder="14"
                              value={subscapular}
                              onChange={(e) => setSubscapular(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="abdomen7">Karın</Label>
                            <Input
                              id="abdomen7"
                              type="number"
                              placeholder="16"
                              value={abdomen7}
                              onChange={(e) => setAbdomen7(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="suprailiac">Suprailiac</Label>
                            <Input
                              id="suprailiac"
                              type="number"
                              placeholder="10"
                              value={suprailiac}
                              onChange={(e) => setSuprailiac(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="thigh7">Uyluk</Label>
                            <Input
                              id="thigh7"
                              type="number"
                              placeholder="18"
                              value={thigh7}
                              onChange={(e) => setThigh7(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Navy Method */}
                    <TabsContent value="navy" className="space-y-4">
                      <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
                        <h4 className="mb-2 font-semibold">
                          Çevre Ölçümleri (cm)
                        </h4>
                        <p className="mb-4 text-muted-foreground text-sm">
                          Mezura ile çevre ölçümlerini yapın. Kaliper
                          gerektirmez.
                        </p>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label htmlFor="waist">Bel Çevresi</Label>
                            <Input
                              id="waist"
                              type="number"
                              placeholder="85"
                              value={waist}
                              onChange={(e) => setWaist(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="neck">Boyun Çevresi</Label>
                            <Input
                              id="neck"
                              type="number"
                              placeholder="38"
                              value={neck}
                              onChange={(e) => setNeck(e.target.value)}
                            />
                          </div>
                          {gender === "female" && (
                            <div className="space-y-2">
                              <Label htmlFor="hip">Kalça Çevresi</Label>
                              <Input
                                id="hip"
                                type="number"
                                placeholder="95"
                                value={hip}
                                onChange={(e) => setHip(e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    {/* Deurenberg (BMI) */}
                    <TabsContent value="deurenberg" className="space-y-4">
                      <div className="rounded-lg border border-warning/20 bg-warning/10 p-4">
                        <h4 className="mb-2 font-semibold">
                          BMI Bazlı Hesaplama
                        </h4>
                        <p className="mb-4 text-muted-foreground text-sm">
                          Sadece boy, kilo, yaş ve cinsiyet bilgileri
                          kullanılır. En kolay yöntem ancak en az hassas.
                        </p>
                        <p className="text-foreground text-xs">
                          Bu yöntem için yukarıdaki boy ve kilo bilgileri
                          yeterlidir.
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                <Button
                  onClick={calculateBodyFat}
                  className="w-full"
                  disabled={!age || !gender}
                >
                  Vücut Yağ Oranını Hesapla
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sonuçlar */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Sonuçlarınız
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results ? (
                  <div className="space-y-6">
                    <div className="rounded-lg bg-primary/10 p-6 text-center">
                      <div className="mb-2 font-bold text-4xl text-primary">
                        {results.bodyFat.toFixed(1)}%
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Vücut Yağ Oranı
                      </div>
                      <div className="mt-1 text-muted-foreground text-xs">
                        {results.method}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Kategori:</h4>
                      <div
                        className={`rounded-lg p-3 text-center ${results.category.color}`}
                      >
                        <div className="font-bold">
                          {results.category.label}
                        </div>
                      </div>
                    </div>

                    {results.leanMass && results.fatMass && (
                      <div className="space-y-3">
                        <h4 className="font-semibold">Vücut Kompozisyonu:</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-lg border border-primary/20 bg-primary/10 p-3 text-center">
                            <div className="font-bold text-primary">
                              {results.leanMass.toFixed(1)} kg
                            </div>
                            <div className="text-muted-foreground text-xs">
                              Yağsız Kütle
                            </div>
                          </div>
                          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-center">
                            <div className="font-bold text-destructive">
                              {results.fatMass.toFixed(1)} kg
                            </div>
                            <div className="text-muted-foreground text-xs">
                              Yağ Kütlesi
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rounded border border-info/20 bg-info/5 p-3 dark:border-info/30 dark:bg-info/10">
                      <p className="text-info-foreground text-sm">
                        <strong>Not:</strong> Sonuçlar yaklaşık değerlerdir.
                        Profesyonel değerlendirme için uzman görüşü alın.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    Hesaplama yapmak için soldaki formu doldurun
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Modern Bilgilendirici İçerik */}
        <div className="mt-16 space-y-12">
          {/* Hero Section */}
          <div className="rounded-2xl border bg-card p-8 md:p-12">
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <h2 className="mb-4 font-bold text-3xl text-foreground">
                  Vücut Kompozisyonunuzu Keşfedin
                </h2>
                <p className="mb-6 text-lg text-muted-foreground">
                  Sadece kilo değil, vücut yağ oranınız gerçek sağlığınızın
                  göstergesi. Bilimsel yöntemlerle doğru analiz yapın.
                </p>
              </div>
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
                    <Scale className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  4 farklı bilimsel hesaplama yöntemi
                </div>
              </div>
            </div>
          </div>

          {/* Method Comparison */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Ruler className="h-8 w-8 " />
                </div>
                <h3 className="font-bold">Jackson-Pollock 3</h3>
              </div>
              <div className="space-y-2 text-foreground text-sm">
                <div className="flex justify-between">
                  <span>Hassaslık:</span>
                  <span className="font-semibold">⭐⭐⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Kolay:</span>
                  <span className="font-semibold">⭐⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Ekipman:</span>
                  <span className="font-semibold text-xs">Kaliper</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Crosshair className="h-8 w-8 text-accent-foreground" />
                </div>
                <h3 className="font-bold text-accent-foreground">
                  Jackson-Pollock 7
                </h3>
              </div>
              <div className="space-y-2 text-foreground text-sm">
                <div className="flex justify-between">
                  <span>Hassaslık:</span>
                  <span className="font-semibold">⭐⭐⭐⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Kolay:</span>
                  <span className="font-semibold">⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Ekipman:</span>
                  <span className="font-semibold text-xs">Kaliper</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Scale className="h-8 w-8 text-secondary-foreground" />
                </div>
                <h3 className="font-bold text-secondary-foreground">
                  Navy Method
                </h3>
              </div>
              <div className="space-y-2 text-foreground text-sm">
                <div className="flex justify-between">
                  <span>Hassaslık:</span>
                  <span className="font-semibold">⭐⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Kolay:</span>
                  <span className="font-semibold">⭐⭐⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Ekipman:</span>
                  <span className="font-semibold text-xs">Mezura</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Calculator className="h-8 w-8" />
                </div>
                <h3 className="font-bold">BMI (Deurenberg)</h3>
              </div>
              <div className="space-y-2 text-foreground text-sm">
                <div className="flex justify-between">
                  <span>Hassaslık:</span>
                  <span className="font-semibold">⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Kolay:</span>
                  <span className="font-semibold">⭐⭐⭐⭐⭐</span>
                </div>
                <div className="flex justify-between">
                  <span>Ekipman:</span>
                  <span className="font-semibold text-xs">Yok</span>
                </div>
              </div>
            </div>
          </div>

          {/* Body Fat Categories */}
          <div className="rounded-2xl border bg-card p-8">
            <h3 className="mb-8 flex items-center justify-center gap-2 text-center font-bold text-2xl text-foreground">
              <Target className="h-6 w-6 text-primary" />
              Vücut Yağ Kategorileri
            </h3>

            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h4 className="mb-4 flex items-center justify-center gap-2 text-center font-semibold text-foreground text-xl">
                  <Mars className="h-5 w-5 text-primary" />
                  Erkekler
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      range: "2-5%",
                      category: "Esansiyel Yağ",
                      color: "bg-slate-500",
                      desc: "Yaşamsal minimum",
                    },
                    {
                      range: "6-13%",
                      category: "Sporcu",
                      color: "bg-green-500",
                      desc: "Atletik performans",
                    },
                    {
                      range: "14-17%",
                      category: "Fitness",
                      color: "bg-blue-500",
                      desc: "Çok iyi form",
                    },
                    {
                      range: "18-24%",
                      category: "Ortalama",
                      color: "bg-warning",
                      desc: "Kabul edilebilir",
                    },
                    {
                      range: "25%+",
                      category: "Obez",
                      color: "bg-destructive",
                      desc: "Sağlık riski",
                    },
                  ].map((item) => (
                    <div className="flex items-center gap-4 rounded-lg border bg-card/50 p-3 transition-colors hover:bg-card/80">
                      <div
                        className={cn(
                          "h-4 w-4 rounded-full",
                          item.color,
                          "ring-2 ring-background",
                        )}
                      ></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">
                            {item.category}
                          </span>
                          <span className="font-mono text-muted-foreground text-sm">
                            {item.range}
                          </span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="mb-4 flex items-center justify-center gap-2 text-center font-semibold text-foreground text-xl">
                  <Venus className="h-5 w-5 text-primary" />
                  Kadınlar
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      range: "10-13%",
                      category: "Esansiyel Yağ",
                      color: "bg-slate-500",
                      desc: "Yaşamsal minimum",
                    },
                    {
                      range: "14-20%",
                      category: "Sporcu",
                      color: "bg-green-500",
                      desc: "Atletik performans",
                    },
                    {
                      range: "21-24%",
                      category: "Fitness",
                      color: "bg-blue-500",
                      desc: "Çok iyi form",
                    },
                    {
                      range: "25-31%",
                      category: "Ortalama",
                      color: "bg-warning",
                      desc: "Kabul edilebilir",
                    },
                    {
                      range: "32%+",
                      category: "Obez",
                      color: "bg-destructive",
                      desc: "Sağlık riski",
                    },
                  ].map((item) => (
                    <div className="flex items-center gap-4 rounded-lg border bg-card/50 p-3 transition-colors hover:bg-card/80">
                      <div
                        className={`h-4 w-4 rounded-full ${item.color} ring-2 ring-background`}
                      ></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">
                            {item.category}
                          </span>
                          <span className="font-mono text-muted-foreground text-sm">
                            {item.range}
                          </span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="rounded-2xl border border-border/50 bg-card p-8">
            <h3 className="mb-6 flex items-center gap-2 font-bold text-2xl text-foreground">
              <Info className="h-6 w-6 text-primary" />
              Doğru Ölçüm İpuçları
            </h3>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Kaliper Kullanımı
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Deri kıvrımını sıkın, kaliperi yerleştirin ve 2 saniye
                      bekleyin
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Tutarlı Ölçümler
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Aynı saatte, aynı koşullarda ölçüm yapın
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Çevre Ölçümleri
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Mezurayı gevşek tutun, nefes verirken ölçün
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Yardım Alın
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Sırt ölçümleri için mutlaka yardım alın
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
